<template>
  <div
    v-if="multi ? !isMultiHidden : !isHidden || modal"
    class="mb-4 rounded-xl p-4 md:p-5 border border-primary-blue-200 bg-gradient-to-r from-primary-blue-50 to-white shadow-md ring-1 ring-primary-blue-100"
  >
    <div v-if="multi" class="flex items-start justify-between gap-3">
      <div class="w-full">
        <div class="text-primary-gray-900 text-sm md:text-base leading-6 font-medium">
          <PostMarkdown :text="currentTip" :max-lines="maxLines" />
        </div>
        <div v-if="tipsCount > 1" class="mt-3 flex items-center justify-between">
          <span class="text-xs text-primary-gray-500">
            {{ currentIndex + 1 }} / {{ tipsCount }}
          </span>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-primary-gray-200 hover:bg-primary-gray-50 shadow-sm"
              aria-label="Previous tip"
              @click="prevTip"
            >
              <icon icon="chevronLeft" width="16" height="16" class="text-primary-gray-700" />
            </button>
            <button
              type="button"
              class="h-8 w-8 inline-flex items-center justify-center rounded-full bg-white border border-primary-gray-200 hover:bg-primary-gray-50 shadow-sm"
              aria-label="Next tip"
              @click="nextTip"
            >
              <icon icon="chevronRight" width="16" height="16" class="text-primary-gray-700" />
            </button>
          </div>
        </div>
      </div>
      <button
        type="button"
        class="cursor-pointer shrink-0 p-1.5 rounded-full hover:bg-primary-gray-100 transition"
        aria-label="Close tips"
        @click="closeMulti"
      >
        <icon icon="cross" width="18" height="18" class="text-primary-gray-600" />
      </button>
    </div>

    <!-- Single mode: respects hide/modal with close and disable-for-days controls -->
    <div v-else-if="!isButtonsModal" class="flex items-start justify-between gap-3">
      <div class="w-full">
        <div class="text-primary-gray-800 text-sm md:text-base leading-6 font-medium">
          <PostMarkdown :text="currentTip" :max-lines="isMobileView ? 3 : 6" />
        </div>
      </div>
      <button
        type="button"
        class="cursor-pointer shrink-0 p-1.5 rounded-full hover:bg-primary-gray-100 transition"
        aria-label="Close tips"
        @click="toggleButtonsModal"
      >
        <icon icon="cross" width="18" height="18" class="text-primary-gray-600" />
      </button>
    </div>
    <div v-if="isButtonsModal" class="flex flex-col items-center justify-between gap-2">
      <div class="w-full">
        <div
          class="inline-flex items-center gap-1 text-sm font-medium text-primary-purple-600 hover:text-primary-purple-700 hover:underline cursor-pointer mb-2"
          @click="toggleButtonsModal"
        >
          <icon icon="arrowLeft" color="primary-purple-600" height="12" width="12" />
          <span>Back</span>
        </div>
        <h4 class="font-semibold text-primary-gray-800 mb-3 text-center">
          Documentation Modal Disabled for
        </h4>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-2 w-full md:w-auto">
        <UIButton
          class="w-full 2xl:text-base xl:text-sm text-xs"
          button="primary"
          type="button"
          @click="hideModalForDays(1)"
        >
          Next 24 hours
        </UIButton>
        <UIButton
          class="w-full 2xl:text-base xl:text-sm text-xs"
          button="primary"
          type="button"
          @click="hideModalForDays(7)"
        >
          Next 7 days
        </UIButton>
        <UIButton
          class="w-full 2xl:text-base xl:text-sm text-xs"
          button="primary"
          type="button"
          @click="hideModalForDays(30)"
        >
          Next 30 days
        </UIButton>
      </div>
    </div>
  </div>
</template>

<script>
import icon from '@components/icons/icon.vue'
import UIButton from '@src/components/UiElements/UIButton.vue'
import PostMarkdown from '@src/components/notice-board/PostMarkdown.vue'
import { mapState } from 'vuex'
export default {
  name: 'DocumentTips',
  components: { icon, UIButton, PostMarkdown },
  props: {
    tips: {
      type: [String, Array],
      default: '',
    },
    modal: {
      type: Boolean,
      default: false,
    },
    multi: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateToggleDocumentationTips'],
  data() {
    return {
      isButtonsModal: false,
      isHidden: false,
      currentIndex: 0,
      isMultiHidden: false,
    }
  },
  computed: {
    ...mapState({
      isMobileView: (state) => state.layout.isMobileView,
    }),
    tipsArray() {
      if (this.multi) {
        return Array.isArray(this.tips) ? this.tips : this.tips ? [this.tips] : []
      }
      return [typeof this.tips === 'string' ? this.tips : '']
    },
    tipsCount() {
      return this.tipsArray.length
    },
    currentTip() {
      return this.tipsArray[this.currentIndex] || ''
    },
    maxLines() {
      return this.isMobileView ? 4 : 8
    },
  },

  watch: {
    modal: {
      handler(newVal) {
        if (this.multi) return
        this.isHidden = !newVal
      },
    },
    tipsArray() {
      if (this.currentIndex > this.tipsCount - 1) this.currentIndex = 0
    },
  },
  created() {
    this.checkHidePreference()
  },

  methods: {
    nextTip() {
      if (!this.tipsCount) return
      this.currentIndex = (this.currentIndex + 1) % this.tipsCount
    },
    prevTip() {
      if (!this.tipsCount) return
      this.currentIndex = (this.currentIndex - 1 + this.tipsCount) % this.tipsCount
    },
    toggleButtonsModal() {
      if (this.multi) return
      const now = Date.now()
      const hideUntil = localStorage.getItem('hideDocumentationModal')
      if (hideUntil && hideUntil > now) {
        this.isHidden = true
        this.$emit('updateToggleDocumentationTips', !this.isHidden)
      } else {
        this.isButtonsModal = !this.isButtonsModal
      }
    },
    hideModalForDays(days) {
      const hideUntil = new Date().getTime() + days * 24 * 60 * 60 * 1000
      localStorage.setItem('hideDocumentationModal', hideUntil.toString())
      this.isButtonsModal = false
      this.isHidden = true
      this.$emit('updateToggleDocumentationTips', !this.isHidden)
    },
    closeMulti() {
      this.isMultiHidden = true
    },
    checkHidePreference() {
      const value = localStorage.getItem('hideDocumentationModal')
      const hideUntil = value ? parseInt(value, 10) : 0
      const now = Date.now()
      if (hideUntil && hideUntil > now) {
        this.isHidden = true
      } else {
        // Clean up expired preference
        if (hideUntil && hideUntil <= now) localStorage.removeItem('hideDocumentationModal')
        this.isHidden = false
      }
      this.$emit('updateToggleDocumentationTips', !this.isHidden)
    },
  },
}
</script>
